import { useFetchUser } from '@/lib/hooks/useAuth.ts'
import { OrganizationMembership, useUserStore } from '@/store/user.ts'
import { useEffect } from 'react'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import type { PluginListenerHandle } from '@capacitor/core'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { reconcilePushDeviceState, syncNativePushToken } from '@/lib/push/registerPush'
import { addListener as addNetworkListener } from '@/lib/platform/networkStatus'

export default function useSyncUser() {
  const { data } = useFetchUser()

  const setUser = useUserStore(state => state.setUser)
  const setLoading = useUserStore(state => state.setLoading)
  const setCurrentOrganizationMembership = useUserStore(
    state => state.setCurrentOrganizationMembership,
  )

  useEffect(() => {
    if (data) {
      setUser(data)
      setLoading(false)

      if (data.organizationMemberships) {
        const organizationMembership: OrganizationMembership | undefined =
          data.organizationMemberships.find(
            //@ts-expect-error @id exists
            memberShip => memberShip?.['@id'] === data.activeMembership,
          )
        if (organizationMembership) {
          setCurrentOrganizationMembership(organizationMembership)
        } else {
          setCurrentOrganizationMembership(data?.organizationMemberships?.[0])
        }
      }
    }
  }, [data, setCurrentOrganizationMembership, setLoading, setUser])

  // Settle whatever this device owes the server: deletes left over from a logout that
  // could not reach it, and a rotated token whose upload failed.
  //
  // Re-run on reconnect and on foreground, not just on load. Both obligations are
  // created by a FAILED request, so the moment worth retrying is when connectivity
  // returns — and a rotated token has no second chance of its own, because FCM only
  // signals genuinely new tokens. Runs here because only the owner may act on their
  // own rows, so it needs an authenticated session belonging to that same user.
  const userId = data?.id
  useEffect(() => {
    if (!userId) return

    const reconcile = () => void reconcilePushDeviceState(userId).catch(() => undefined)
    reconcile()

    // BOTH signals, because they do not fire on the same platforms. The WebView's
    // `online` event is the web story; on native, connectivity comes back through
    // Capacitor's `networkStatusChange` — the same source the offline queue uses —
    // and relying on `online` alone would leave a native device holding a rotated
    // token it never retries, which is exactly the silent push death this reconcile
    // exists to prevent.
    let disposed = false
    let networkListener: PluginListenerHandle | undefined
    void addNetworkListener('networkStatusChange', status => {
      if (status.connected) reconcile()
    })
      .then(handle => {
        if (disposed) void handle.remove()
        else networkListener = handle
      })
      .catch(() => undefined)

    window.addEventListener('online', reconcile)
    document.addEventListener('visibilitychange', reconcile)
    return () => {
      disposed = true
      void networkListener?.remove()
      window.removeEventListener('online', reconcile)
      document.removeEventListener('visibilitychange', reconcile)
    }
  }, [userId])

  // Native only: re-upload the FCM token whenever it rotates, so an opted-in
  // device keeps receiving push (see syncNativePushToken — it's a no-op unless
  // THIS user previously enabled push on THIS device). Subscribed once the user is
  // loaded so the POST /push_devices is authenticated, and the user id is passed
  // so a rotation can never re-point the row at somebody who never opted in.
  useEffect(() => {
    if (!data?.id || !isNativePlatform()) return

    const userId = data.id
    let disposed = false
    let listener: PluginListenerHandle | undefined
    void FirebaseMessaging.addListener('tokenReceived', event => {
      void syncNativePushToken(event.token, userId).catch(() => undefined)
    })
      .then(handle => {
        if (disposed) void handle.remove()
        else listener = handle
      })
      .catch(() => undefined)

    return () => {
      disposed = true
      void listener?.remove()
    }
    // Keyed on the user ID, not the whole `data` object: the listener closes over
    // that id, so it must be torn down and re-subscribed when the ACCOUNT changes
    // — but not on every unrelated refetch of the same user.
  }, [data?.id])

  return null
}
